import React from 'react'
import styled from 'styled-components'
import Button from 'components/Button'
import { MapPin, Plus, Upload, Sparkles } from 'lucide-react'

interface PortfolioLocationsEmptyStateProps {
	onAddExisting?: () => void
	onCreateNew?: () => void
	onUploadList?: () => void
	isOwner: boolean
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3rem 1.5rem;
	text-align: center;
	background: #ffffff;
	border: 1px dashed rgba(0, 0, 0, 0.08);
	border-radius: 12px;
	color: #2d3748;
	width: 100%;
`

const IconWrapper = styled.div`
	width: 96px;
	height: 96px;
	border-radius: 9999px;
	background: rgba(0, 128, 254, 0.08);
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 24px;

	svg {
		width: 48px;
		height: 48px;
		color: #0080fe;
	}
`

const Title = styled.h3`
	font-size: 1.375rem;
	font-weight: 700;
	margin-bottom: 0.5rem;
`

const Description = styled.p`
	max-width: 520px;
	margin: 0 auto 2rem;
	line-height: 1.6;
	color: #4a5568;
`

const Actions = styled.div`
	display: flex;
	gap: 0.75rem;
	flex-wrap: wrap;
	justify-content: center;
`

const Tip = styled.div`
	margin-top: 2rem;
	padding: 1rem 1.25rem;
	background: #f7fafc;
	border-radius: 12px;
	max-width: 460px;
	color: #4a5568;
	font-size: 0.95rem;

	h4 {
		margin: 0 0 0.5rem;
		font-size: 0.9rem;
		font-weight: 600;
		color: #2d3748;
	}
`

const PortfolioLocationsEmptyState: React.FC<PortfolioLocationsEmptyStateProps> = ({
	onAddExisting,
	onCreateNew,
	onUploadList,
	isOwner,
}) => {
	const showActions = isOwner && (onAddExisting || onCreateNew || onUploadList)

	return (
		<Container>
			<IconWrapper>
				<MapPin className="w-4 h-4" />
			</IconWrapper>
			<Title>No Locations Yet</Title>
			<Description>
				Start building this portfolio by adding existing locations or creating a new one. You can also upload a list to
				add multiple locations at once.
			</Description>

			{showActions && (
				<Actions>
					{onAddExisting && (
						<Button variant="primary" onClick={onAddExisting}>
							<span className="flex items-center gap-2">
								<Plus className="w-4 h-4" />
								Add Existing Location
							</span>
						</Button>
					)}
					{onCreateNew && (
						<Button variant="primary" onClick={onCreateNew}>
							<span className="flex items-center gap-2">
								<Sparkles className="w-4 h-4" />
								Create New Location
							</span>
						</Button>
					)}
					{onUploadList && (
						<Button variant="ghost" onClick={onUploadList}>
							<span className="flex items-center gap-2">
								<Upload className="w-4 h-4" />
								Upload Locations List
							</span>
						</Button>
					)}
				</Actions>
			)}

			<Tip>
				<h4>💡 Tip</h4>
				<p>
					Keep addresses, contacts, and key dates handy—organizing locations in a portfolio helps everyone stay in sync
					when deals move fast.
				</p>
			</Tip>
		</Container>
	)
}

export default PortfolioLocationsEmptyState
